import { Box, ModalBody, ModalFoot, ModalHead } from '@dixa/lib-shared-habla';
import memoize from 'memoize-one';
import { useCallback } from 'react';
import { connect } from 'react-redux';
import {
  ErrorOther,
  FieldArray,
  Form,
  FormErrors,
  formValueSelector,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';
import { v4 as uuid } from 'uuid';

import { RootReducerStateT } from 'app/reducers/rootReducer';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import {
  FlowMessengerNodeURLTrigger,
  Route,
} from 'components/settings/components/sections/dixaMessenger/views/flow/flow.types';
import { FieldRepeater, RepeaterDivider } from 'fable/fieldRepeater';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import { FieldRoutes } from './components/fieldRoutes/fieldRoutes';
import { validate } from './validate';

export interface Validation extends FormErrors {
  routes?: ErrorOther | Partial<Route>[];
}

export interface URLTriggerModalFormValues {
  routes: (Omit<Route, 'urlPrefix'> & { urlPrefix?: Route['urlPrefix'] })[];
}

const getDefaultRowProps = () => ({ urlPrefix: '', nextNodeId: uuid() });

interface OwnProps
  extends FlowManagerEditModalComponentT<FlowMessengerNodeURLTrigger> {
  routes: Route[];
}

type Props = OwnProps & InjectedFormProps<URLTriggerModalFormValues, OwnProps>;

const URLTriggerModalImpl = (props: Props): JSX.Element => {
  const {
    node,
    nodeLabel,
    triggerClose,
    handleSubmit,
    destroy,
    replaceNode,
    dirty: isChanged,
  } = props;

  const closeModal = useCallback(() => {
    destroy();
    triggerClose();
  }, [destroy, triggerClose]);

  const handleSaveClick = useCallback(
    (values: URLTriggerModalFormValues) => {
      const defaultNextNodeId = node.payload?.defaultNextNodeId || uuid();

      const nodeOptions: FlowMessengerNodeURLTrigger = {
        ...node,
        badges: [],
        icon: '',
        isAction: false,
        isEditable: true,
        isRemovable: true,
        nodeType: FlowNodeTypes.URL,
        payload: {
          routes: values.routes.map((route) => ({ urlPrefix: '', ...route })),
          defaultNextNodeId,
        },
        overlay: [],
        text: '',
      };

      replaceNode(nodeOptions);
      triggerClose();
    },
    [node, replaceNode, triggerClose],
  );

  return (
    <Box width="600px">
      <Form onSubmit={handleSubmit(handleSaveClick)}>
        <ModalHead title={nodeLabel} onClose={closeModal}>
          URL triggers can be used to change how the Dixa Messenger routes based
          on which URL the customer is visiting, when he or she starts the
          conversation.
          <br />
          <br />
          E.g. you can have one queue for your check-out pages and one for
          everything else. <i>https://example.com/support</i> or{' '}
          <i>https://example.com/checkout</i>
          <br />
          <br />* Note that the URL trigger node will try each url you provide
          here one by one, and route based on the{' '}
          <strong>first one that matches</strong>. Make sure you enter your{' '}
          <strong>more specific URLs first.</strong>
        </ModalHead>
        <ModalBody>
          <FieldArray
            buttonProps={{ placement: 'outside', label: 'Add URL' }}
            component={FieldRepeater}
            defaultRowProps={getDefaultRowProps}
            divider={<RepeaterDivider />}
            rowProps={{}}
            name="routes"
            rowComponent={FieldRoutes}
            title="URLs"
          />
        </ModalBody>
        <ModalFoot
          confirmOnClick={handleSubmit(handleSaveClick)}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{ disabled: !isChanged }}
          cancelOnClick={closeModal}
        />
      </Form>
    </Box>
  );
};

const formName = 'messengerURLTriggerModal';
const getMemoizedUUID = memoize((_node: OwnProps['node']) => uuid());

const mapStateToProps = (state: RootReducerStateT, ownProps: OwnProps) => {
  const formVal = formValueSelector(formName);
  const { node } = ownProps;

  return {
    initialValues: {
      routes:
        node.payload?.routes ||
        ([
          {
            urlPrefix: '',
            // Use memoized function so UUID doesn't constantly change
            nextNodeId: getMemoizedUUID(node),
          },
        ] as Route[]),
    },
    routes: formVal(state, 'routes'),
  };
};

export const URLTriggerModal = connect(mapStateToProps)(
  reduxForm<URLTriggerModalFormValues, Props>({
    form: formName,
    enableReinitialize: true,
    validate,
  })(URLTriggerModalImpl),
);
